import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { isEmpty } from "lodash";

// Redux
import { connect } from "react-redux";

// Components
import IkTable from "@Components/IkTable";
import IKModal from "@Components/IkModal";

// Utils
import { dataHandler } from "@Utils";

// Modals
import TemplateCrud from "./Modals";
import ConfirmModal from "@Modals/ConfirmModal";

// Styles
import styles from "./styles.module.css";
import genericAction from "@Redux/Actions/generic";

const metaStatusMap = {
	LOCAL: "Local",
	PENDING: "Pendiente",
	APPROVED: "Aprobada",
	REJECTED: "Rechazada",
	IN_DELETION: "En eliminación",
	DELETED: "Eliminada",
	PAUSED: "Pausada",
};

const mapMetaStatus = (items) => {
	return items.map((item) => {
		return {
			...item,
			metaStatus: metaStatusMap[item.metaStatus] || item.metaStatus || "",
		};
	});
};

const Templates = ({ dispatch, userName, companyName, onClose }) => {
	const [tableValue, setTableValue] = useState([]);
	const [modalOption, setModalOption] = useState("Add");
	const [filteredCount, setFilteredCount] = useState(null);
	const [filteredItems, setFilteredItems] = useState([]);
	const [filteredPrintData, setFilteredPrintData] = useState([]);
	const [openModalAdd, setOpenModalAdd] = useState(false);
	const [openConfirmModal, setOpenConfirmModal] = useState(false);
	const [itemDelete, setItemDelete] = useState({});
	const defaultParams = { page: 1, orderKey: "id", orderDir: "desc", rows: 15 };

	useEffect(() => {
		(async () => {
			const templatePromise = dataHandler({
				dispatch,
				url: "templates",
				params: defaultParams,
			});
			const [templateResponse] = await Promise.all([templatePromise]);
			if (isEmpty(templateResponse.items)) {
				setOpenModalAdd(true);
			}
			setFilteredCount(templateResponse.count);
			setFilteredItems(mapMetaStatus(templateResponse.items));
		})();
	}, []);

	const modalData = (name, item) => {
		setTableValue(item);
		setModalOption(name);
		setOpenModalAdd(true);
	};

	const handleButton = (name, item) => {
		switch (name) {
			case "Watch":
				modalData(name, item);
				break;
			case "Edit":
				modalData(name, item);
				break;
			case "Add":
				modalData(name, {});
				break;
			case "Eraser":
				setOpenConfirmModal(true);
				setItemDelete(item);
				break;
			default:
				break;
		}
	};

	const handleDelete = () => {
		dispatch(genericAction({ method: "DELETE", route: `templates/${itemDelete.id}`, onSuccess: refresh }));
		setOpenConfirmModal(false);
	};

	const setRowStyle = (item) => {
		if (!item.isActive) {
			return { backgroundColor: "rgba(255, 0, 0, 0.24)" };
		}
		return null;
	};

	const handlerData = async (params, printable) => {
		const data = await dataHandler({ dispatch, url: "templates", params });
		if (printable) {
			setFilteredPrintData(data.items);
		} else {
			setFilteredItems(mapMetaStatus(data.items));
			setFilteredCount(data.count);
		}
	};

	const refresh = async () => {
		const profilesResponse = await dataHandler({
			dispatch,
			url: "templates",
			params: defaultParams,
		});
		setFilteredCount(profilesResponse.count);
		setFilteredItems(mapMetaStatus(profilesResponse.items));
	};

	return (
		<IKModal onClose={onClose} title={`Plantillas - ${companyName}`} iconName="whatsapp" noMode onSuccess={null} fullHeight>
			<div className={styles.container}>
				<div className={styles.containerTableGroup}>
					<div className={styles.tableGroupList}>
						<IkTable
							data={filteredItems}
							columns={[
								{
									label: "Acciones",
									action: [
										{
											content: "trash",
											color: "#b43333",
											onClick: "Eraser",
											type: "icon",
											visible: () => {
												return false;
											},
										},
										{
											content: "edit",
											color: "#3f7ea3",
											onClick: "Edit",
											type: "icon",
										},
									],
									dataPosition: "center",
									width: 1,
									canFilter: false,
									canSort: false,
								},
								{
									label: "Canal",
									key: "channel.label",
									format: "string",
									headerPosition: "left",
									width: 1,
								},
								{
									label: "ID",
									key: "id",
									format: "string",
									dataPosition: "center",
									width: 1,
								},
								{
									label: "Activo",
									key: "isActive",
									format: "boolean",
									boolLabels: { true: "Activo", false: "Inactivo" },
									dataPosition: "center",
									width: 1,
								},

								{
									label: "Nombre",
									key: "title",
									format: "string",
									headerPosition: "left",
									width: 3,
								},

								{
									label: "Categoría",
									key: "category",
									format: "string",
									headerPosition: "left",
									width: 15,
								},
								{
									label: "Estado Meta",
									key: "metaStatus",
									format: "string",
									dataPosition: "left",
									width: 1,
									boolLabels: { true: "Aprobada", false: "Rechazada" },
								},
							]}
							totalRows={filteredCount}
							filteredPrintData={filteredPrintData}
							defaultParams={defaultParams}
							dataHandler={handlerData}
							handlerTable={handleButton}
							addButton={{ label: "Nueva" }}
							searcher={{ width: "40rem", filterButton: false, searchable: false }}
							setRowStyle={setRowStyle}
							print={{ printHeader: { company: companyName, title: "Usuarios", user: userName }, printable: false }}
						/>
					</div>
				</div>
			</div>

			{openConfirmModal && (
				<ConfirmModal
					visible
					icon="exclamation circle"
					header="Confirmar"
					buttonLabel="Eliminar"
					content="Está seguro que desea eliminar la plantilla"
					focus={itemDelete.title}
					onConfirm={() => {
						handleDelete();
					}}
					onDismiss={() => {
						return setOpenConfirmModal(false);
					}}
				/>
			)}

			{openModalAdd && (
				<TemplateCrud
					tableValue={tableValue}
					refresh={refresh}
					metaStatus={tableValue?.metaStatus}
					onClose={() => {
						setOpenModalAdd(false);
					}}
					modalCrud={modalOption}
				/>
			)}
		</IKModal>
	);
};
function mapStateToProps({ user, company }) {
	return {
		user,
		userName: user.name,
		companyName: company.name,
	};
}

Templates.propTypes = {
	companyName: PropTypes.string,
	userName: PropTypes.string,
	dispatch: PropTypes.func,
	onClose: PropTypes.func,
};
export default connect(mapStateToProps)(Templates);
